import * as React from 'react'
import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Spinner } from './spinner'

/**
 * shadcn's button on Base UI. Nothing else in the app has to know which engine is underneath.
 *
 * A NAVIGATION link styled as a button is `<Link className={buttonVariants(…)}>`, not
 * `<Button render={<Link />}>`: Base UI gives a non-native button `role="button"`, which turns a
 * link into a button for assistive technology and for every test that queries by role.
 *
 * `pending` is for the button that started the work, while it runs: a spinner in place of the
 * button's icons (an icon-only button keeps its size), clicks refused, `aria-busy` set. It stays focusable, so a keyboard user who pressed it is not
 * dropped to the top of the page — a plain `disabled` would blur it.
 */
const buttonVariants = cva(
    "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
                destructive: 'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
                outline: 'border border-border bg-card text-foreground hover:bg-secondary',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
                ghost: 'hover:bg-secondary hover:text-secondary-foreground',
                link: 'text-primary underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-10 px-5 py-2 has-[>svg]:px-4',
                sm: 'h-9 rounded-md px-3 has-[>svg]:px-2.5',
                lg: 'h-12 rounded-lg px-6 text-base has-[>svg]:px-5',
                icon: 'size-10',
            },
        },
        defaultVariants: { variant: 'default', size: 'default' },
    },
)

function Button({
    className, variant, size, render, nativeButton, pending = false, disabled, children, ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants> & { pending?: boolean }) {
    return (
        <ButtonPrimitive
            data-slot="button"
            render={render}
            nativeButton={nativeButton ?? render === undefined}
            className={cn(buttonVariants({ variant, size, className }), pending && '[&>svg:not([data-slot=spinner])]:hidden')}
            disabled={disabled || pending}
            focusableWhenDisabled={pending || undefined}
            aria-busy={pending || undefined}
            {...props}
        >
            {pending && <Spinner />}
            {children}
        </ButtonPrimitive>
    )
}

export { Button, buttonVariants }
